import { useEffect, useMemo, useState } from "react";
import * as Utilities from "../../data/utilities";
import {
  API_ENDPOINT,
  getPdfMetaResource,
  getResource,
  getResourcePage,
  HEADERS,
} from "../../data/request";
import { File } from "../../types/File";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TextField,
  CircularProgress,
  Button,
  Box,
  Typography,
  TablePagination,
  MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { TEXT_SECONDARY } from "../colors";

const ROWS_PER_PAGE = 15;

export const FillingHistoryTable = (props: { link: string }) => {
  const { link } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [orderBy, setOrderBy] = useState<keyof File>("date");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const downloadPDF = async (file: any) => {
    try {
      const response = await getPdfMetaResource(file.links.document_metadata);

      fetch(API_ENDPOINT + response.links.document, {
        headers: HEADERS,
      }).then(async (res) => {
        const jsonData = await res.json();
        window.open(jsonData.location);
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const doc = await getResource(link);
      setFiles(doc?.items ?? []);
      setTotalCount(doc?.total_count ?? doc?.items?.length ?? 0);
      setLoading(false);
    };

    fetchData();
  }, [link]);

  const categories = useMemo(
    () => Array.from(new Set(files.map((item) => item.category).filter(Boolean))).sort(),
    [files]
  );

  const visibleFiles = useMemo(() => {
    let result = files;

    if (category !== "all") {
      result = result.filter((item) => item.category === category);
    }

    if (filter) {
      const lower = filter.toLowerCase();
      result = result.filter(
        (item) =>
          item.date?.toLowerCase().includes(lower) ||
          item.description?.toLowerCase().includes(lower)
      );
    }

    return [...result].sort((a, b) => {
      const aVal = a[orderBy] ?? "";
      const bVal = b[orderBy] ?? "";
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return order === "asc" ? comparison : -comparison;
    });
  }, [files, filter, category, orderBy, order]);

  const handleSort = (property: keyof File) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFilterChange = (newValue: string) => {
    setFilter(newValue);
    setPage(0);
  };

  const handleCategoryChange = (newValue: string) => {
    setCategory(newValue);
    setPage(0);
  };

  // Next/Previous is the only pagination control the user sees - if paging
  // forward runs past what's currently loaded, fetch the next batch from the
  // server first, then advance. Companies House caps each call at 100 items
  // regardless of what's requested, so a big filing history needs several of
  // these fetches to reach the end, but the user only ever sees one control.
  const handlePageChange = async (newPage: number) => {
    const rowsNeeded = (newPage + 1) * ROWS_PER_PAGE;
    if (rowsNeeded > visibleFiles.length && files.length < totalCount) {
      setLoadingMore(true);
      const doc = await getResourcePage(link, files.length);
      setFiles((prev) => [...prev, ...(doc?.items ?? [])]);
      setLoadingMore(false);
    }
    setPage(newPage);
  };

  const pagedFiles = visibleFiles.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  // The true total is only knowable in advance when nothing's filtered out -
  // Companies House gives us that number (totalCount) up front, so use it
  // directly rather than a guess that grows as more pages load. Once a
  // filter/category narrows the results, we genuinely can't know the final
  // filtered count until everything's been fetched, so (matching Gmail's
  // "1-50 of many") fall back to MUI's built-in unknown-count mode (-1)
  // instead of showing a number that changes every time Next is clicked.
  const haveEverything = files.length >= totalCount;
  const isFiltered = filter !== "" || category !== "all";
  const paginationCount = haveEverything ? visibleFiles.length : isFiltered ? -1 : totalCount;

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Box style={{ padding: 20 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
          No filing history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box style={{ padding: "20px 10px 10px" }}>
      {files.length > 10 && (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "0 10px 10px",
          }}
          sx={{ flexDirection: { xs: "column", sm: "row" } }}
        >
          <TextField
            label="Filter by date or description"
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            fullWidth
            size="small"
          />
          {categories.length > 1 && (
            <TextField
              select
              label="Type"
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              size="small"
              style={{ minWidth: 200 }}
            >
              <MenuItem value="all">All types</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {Utilities.getValueDisplay(cat)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === "date"}
                direction={orderBy === "date" ? order : "asc"}
                onClick={() => handleSort("date")}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "description"}
                direction={orderBy === "description" ? order : "asc"}
                onClick={() => handleSort("description")}
              >
                Description
              </TableSortLabel>
            </TableCell>
            <TableCell>Download</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedFiles.map((item) => (
            <TableRow key={item.transaction_id}>
              <TableCell>{Utilities.getDateDisplay(item.date)}</TableCell>
              <TableCell>{Utilities.getValueDisplay(item.description)}</TableCell>
              <TableCell>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadPDF(item)}
                >
                  PDF
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        {loadingMore && <CircularProgress size={16} style={{ marginRight: 12 }} />}
        <TablePagination
          component="div"
          count={paginationCount}
          page={page}
          onPageChange={(_, newPage) => handlePageChange(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
          nextIconButtonProps={{ disabled: loadingMore }}
          labelDisplayedRows={({ from, to, count }) =>
            count === -1 ? `${from}–${to} of many` : `${from}–${to} of ${count}`
          }
        />
      </Box>
    </Box>
  );
};

export default FillingHistoryTable;
